import { CreateOrganizationDialog } from "@/components/organization/create-organization-dialog";
import { OrganizationAvatar } from "@/components/organization/organization-avatar";
import { captureEvent } from "@/lib/analytics-loader";
import { useSuspenseOrganizationList } from "@/lib/queries/organization";
import { useSessionQuery, type SessionData } from "@/lib/queries/session";
import { trpcClient } from "@/lib/trpc";
import { getOrganizationType } from "@/lib/utils/organization";
import { Role } from "@repo/core";
import { Button, Card, CardContent } from "@repo/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Loader2, Plus, Shield } from "lucide-react";
import { Suspense, useState } from "react";
import { PendingInvitationsList } from "./pending-invitations-list";

interface SelectOrganizationProps {
  showLogout?: boolean;
  hideCreateOption?: boolean;
  alert?: {
    title: string;
    description: string;
    variant?: "default" | "destructive";
  };
}

export function SelectOrganization({
  showLogout = true,
  hideCreateOption = false,
  alert,
}: SelectOrganizationProps) {
  const { data: session } = useSessionQuery();
  const { data: organizations } = useSuspenseOrganizationList();
  const calculateOrgs =
    organizations?.slice().sort((a, b) => a.name.localeCompare(b.name)) || [];
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const createAutoMutation = useMutation({
    mutationFn: () => trpcClient.organization.createAuto.mutate(),
    onSuccess: async (newOrg) => {
      // Invalidate org list to refresh UI (though we redirect mostly)
      await queryClient.invalidateQueries({
        queryKey: [["organization", "list"]],
      });
      captureEvent("organization_auto_created", {
        organizationId: newOrg.id,
        slug: newOrg.slug,
        name: newOrg.name,
      });
      navigate({ to: `/o/${newOrg.slug}/select-role` });
    },
    onError: (error) => {
      // Fallback to manual creation dialog if auto fails (e.g. conflict)
      console.error("Auto-create failed", error);
      setCreateOpen(true);
    },
  });

  const handleCreateAccount = () => {
    // If no organizations, auto-create one and redirect to role selection
    if (calculateOrgs.length === 0) {
      createAutoMutation.mutate();
      return;
    }
    setCreateOpen(true);
    captureEvent("create_organization_initiated", {
      location: "select_org_page",
    });
  };

  return (
    <SelectOrganizationView
      alert={alert}
      showLogout={showLogout}
      hideCreateOption={hideCreateOption}
      session={session}
      organizations={calculateOrgs as unknown as OrganizationSummary[]}
      onCreateAccount={handleCreateAccount}
      onNavigate={(path) => navigate({ to: path })}
      createPending={createAutoMutation.isPending}
      createOpen={createOpen}
      setCreateOpen={setCreateOpen}
    />
  );
}

// ============================================================================
// View Component (Exported for Storybook)
// ============================================================================

interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  metadata: string | null | undefined;
}

interface SelectOrganizationViewProps {
  alert?: {
    title: string;
    description: string;
    variant?: "default" | "destructive";
  };
  showLogout: boolean;
  hideCreateOption: boolean;
  session: SessionData | null | undefined;
  organizations: OrganizationSummary[];
  onCreateAccount: () => void;
  onNavigate: (path: string) => void;
  createPending: boolean;
  createOpen: boolean;
  setCreateOpen: (open: boolean) => void;
}

export function SelectOrganizationView({
  alert,
  showLogout,
  hideCreateOption,
  session,
  organizations,
  onCreateAccount,
  onNavigate,
  createPending,
  createOpen,
  setCreateOpen,
}: SelectOrganizationViewProps) {
  return (
    <div className="w-full max-w-md space-y-4">
      <Card>
        <CardContent className="grid gap-2 p-4 pt-4">
          {alert && (
            <div
              className={`mb-4 flex gap-3 rounded-lg border p-4 text-sm ${
                alert.variant === "destructive"
                  ? "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive"
                  : "border-border text-foreground"
              }`}
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <h5 className="font-medium leading-none tracking-tight">
                  {alert.title}
                </h5>
                <div className="text-sm [&_p]:leading-relaxed opacity-90">
                  {alert.description}
                </div>
              </div>
            </div>
          )}

          {session?.session &&
            session?.user &&
            (session.user.role === Role.Admin ||
              session.user.role === Role.SuperAdmin) && (
              <>
                <div className="text-xs font-medium text-muted-foreground px-2 pt-2">
                  Aspire Admin
                </div>
                <Button
                  variant="outline"
                  className="w-full justify-start h-14 px-4 hover:bg-muted/50 transition-all font-normal border-destructive hover:border-destructive min-w-0 overflow-hidden"
                  asChild
                >
                  <Link to="/admin">
                    <div className="mr-3 relative flex shrink-0 items-center justify-center rounded-md border size-8">
                      <img
                        src="/app-assets/aspire/logos/aspire_logo_admin.jpeg"
                        alt="Aspire Admin"
                        className="rounded-sm size-6 object-cover"
                      />
                      <div className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full border size-4 bg-destructive text-primary-foreground ">
                        <Shield className="size-2.5!" />
                      </div>
                    </div>
                    <div className="flex flex-col items-start truncate min-w-0 flex-1">
                      <span className="font-medium truncate w-full text-left">
                        Aspire Admin
                      </span>
                      <span className="text-xs text-muted-foreground truncate w-full text-left">
                        System Administration
                      </span>
                    </div>
                  </Link>
                </Button>
                <div className="h-px bg-border my-2" />
              </>
            )}

          <Suspense fallback={null}>
            <PendingInvitationsList
              onEmptyStateAction={() => setCreateOpen(true)}
            />
          </Suspense>

          {organizations && organizations.length > 0 ? (
            (() => {
              // Separate agencies from creators using shared utility
              const { agencies, creators } = organizations.reduce(
                (acc, org) => {
                  const orgType = getOrganizationType(org.metadata);
                  if (orgType === "agency") {
                    acc.agencies.push(org);
                  } else {
                    acc.creators.push(org);
                  }
                  return acc;
                },
                {
                  agencies: [] as typeof organizations,
                  creators: [] as typeof organizations,
                },
              );

              // Sort each group alphabetically
              agencies.sort((a, b) => a.name.localeCompare(b.name));
              creators.sort((a, b) => a.name.localeCompare(b.name));

              return (
                <>
                  {agencies.length > 0 && (
                    <>
                      <div className="text-xs font-medium text-muted-foreground px-2 pt-2">
                        Agencies
                      </div>
                      {agencies.map((org) => (
                        <Button
                          key={org.id}
                          variant="outline"
                          className="w-full justify-start h-14 px-4 hover:border-primary/50 hover:bg-muted/50 transition-all font-normal min-w-0 overflow-hidden"
                          onClick={() => onNavigate(`/o/${org.slug}`)}
                        >
                          <OrganizationAvatar
                            organization={org}
                            size="md"
                            className="mr-3"
                          />
                          <div className="flex flex-col items-start truncate min-w-0 flex-1">
                            <span className="font-medium truncate w-full text-left">
                              {org.name}
                            </span>
                            <span className="text-xs text-muted-foreground truncate w-full text-left">
                              {org.slug}
                            </span>
                          </div>
                        </Button>
                      ))}
                    </>
                  )}

                  {creators.length > 0 && (
                    <>
                      {agencies.length > 0 && (
                        <div
                          className="h-px bg-border my-2"
                          data-testid="org-group-separator"
                        />
                      )}
                      <div className="text-xs font-medium text-muted-foreground px-2 pt-2">
                        Creators
                      </div>
                      {creators.map((org) => (
                        <Button
                          key={org.id}
                          variant="outline"
                          className="w-full justify-start h-14 px-4 hover:border-primary/50 hover:bg-muted/50 transition-all font-normal min-w-0 overflow-hidden"
                          onClick={() => onNavigate(`/o/${org.slug}`)}
                        >
                          <OrganizationAvatar
                            organization={org}
                            size="md"
                            className="mr-3"
                          />
                          <div className="flex flex-col items-start truncate min-w-0 flex-1">
                            <span className="font-medium truncate w-full text-left">
                              {org.name}
                            </span>
                            <span className="text-xs text-muted-foreground truncate w-full text-left">
                              {org.slug}
                            </span>
                          </div>
                        </Button>
                      ))}
                    </>
                  )}
                </>
              );
            })()
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground border-2 border-dashed rounded-lg space-y-2 p-4">
              <p>You don't have any accounts yet.</p>
              <p>
                Create a new account to get started, or ask for an invitation to
                be sent to you.
              </p>
            </div>
          )}
          {(!hideCreateOption ||
            (organizations && organizations.length === 0)) && (
            <Button
              variant="default"
              className="w-full mt-4"
              onClick={onCreateAccount}
              disabled={createPending}
            >
              {createPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Account
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {showLogout && (
        <div className="text-center">
          <Button
            variant="link"
            className="text-muted-foreground"
            onClick={() => onNavigate("/logout")}
          >
            Sign in with a different email
          </Button>
        </div>
      )}
      <CreateOrganizationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </div>
  );
}
